import React, { useState, useEffect, useRef } from "react";

/* ─── Inline CSS — Light theme: off-white background, black & grey text ──── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:           #f4f3f1;      /* warm off-white page background      */
    --bg-box:       #eeede9;      /* slightly darker box background       */
    --bg-box-hover: #e6e4df;      /* box hover state                      */
    --border:       #d0cdc8;      /* subtle warm grey borders             */
    --text-primary: #111111;      /* near-black — headings & labels       */
    --text-body:    #2e2e2e;      /* dark grey — body copy                */
    --text-muted:   #777777;      /* medium grey — hints, subtitles       */
    --text-faint:   #aaaaaa;      /* light grey — numbers, taglines       */
    --accent-line:  #111111;      /* thin accent line on hover            */
  }

  html, body, #root {
    height: 100%;
    background: var(--bg);
    color: var(--text-primary);
    font-family: 'Montserrat', sans-serif;
    font-weight: 300;
    letter-spacing: 0.05em;
    overflow-x: hidden;
  }

  /* ─── Layout ──────────────────────────────────────────────────────────── */
  .site-wrapper {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  /* ─── Top Bar ──────────────────────────────────────────────────────────── */
  .top-bar {
    width: 100%;
    padding: 1rem 4rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--border);
    background: var(--bg-box);
    opacity: 0;
    transform: translateY(-16px);
    animation: fadeDown 1s cubic-bezier(0.22, 1, 0.36, 1) 0s forwards;
  }

  .top-bar-left {
    display: flex;
    align-items: center;
    gap: 2rem;
  }

  .top-logo {
    text-decoration: none;
    display: flex;
    align-items: center;
  }

  .logo {
    text-decoration: none;
    display: flex;
    align-items: center;
  }

  .contact-info {
    display: flex;
    align-items: center;
    gap: 2rem;
  }

  .contact-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.65rem;
    letter-spacing: 0.1em;
    color: var(--text-body);
    text-decoration: none;
    transition: color 0.3s ease;
  }

  .contact-item:hover {
    color: var(--text-primary);
  }

  .contact-icon {
    font-size: 0.9rem;
    color: var(--text-muted);
  }

  .whatsapp-btn {
    padding: 0.6rem 1.5rem;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-body);
    font-size: 0.6rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .whatsapp-btn:hover {
    background: #25D366;
    color: white;
    border-color: #25D366;
  }

  /* ─── Header ──────────────────────────────────────────────────────────── */
  .site-header {
    width: 100%;
    padding: 3rem 4rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-bottom: 1px solid var(--border);
    opacity: 0;
    transform: translateY(-16px);
    animation: fadeDown 1s cubic-bezier(0.22, 1, 0.36, 1) 0.2s forwards;
  }

  .header-tagline {
    font-size: 0.6rem;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--text-faint);
  }

  /* ─── Logo image ──────────────────────────────────────────────────────── */
  .logo-img {
    height: 48px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  .top-logo-img {
    height: 36px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  /* ─── Hero grid ───────────────────────────────────────────────────────── */
  .hero-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    width: 100%;
  }

  /* Row 1: Interiors + Real Estate side by side */
  .service-box {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 3rem 3.5rem;
    min-height: 45vh;
    cursor: pointer;
    text-decoration: none;
    overflow: hidden;
    background: var(--bg-box);
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    border-bottom: 1px solid var(--border);
    opacity: 0;
    transform: translateY(24px);
    transition: all 0.5s ease;
  }

  .service-box::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.55);
    transition: background 0.5s ease;
    z-index: 1;
  }

  /* Box 01 — Interiors */
  .service-box.box-interiors {
    background-image: url('https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=1600');
    border-right: 1px solid var(--border);
    animation: fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.5s forwards;
  }

  /* Box 02 — Real Estate */
  .service-box.box-realestate {
    background-image: url('/realestatefrontpage.jpeg');
    animation: fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.7s forwards;
  }

  /* Box 03 — Services: half width, half-screen height */
.service-box.box-services {
    /* Changed from 1 / -1 (full width) to span only 1 column (half width) */
    grid-column: span 1; 
    min-height: 50vh;
    /* Updated to a high-quality maintenance/service image */
    background-image: url('https://images.pexels.com/photos/5463575/pexels-photo-5463575.jpeg?auto=compress&cs=tinysrgb&w=1600');
    background-size: cover;
    background-position: center;
    animation: fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 0.9s forwards;
  }

  .service-box:hover::before { background: rgba(0, 0, 0, 0.40); }

  .box-number {
    position: absolute;
    top: 2.5rem;
    left: 3.5rem;
    font-size: 0.55rem;
    letter-spacing: 0.4em;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    z-index: 2;
  }

  .box-line-top {
    position: absolute;
    top: 0;
    left: 0;
    height: 2px;
    background: white;
    width: 0;
    transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
    z-index: 3;
  }

  .service-box:hover .box-line-top { width: 100%; }

  .box-label {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(3rem, 6vw, 5.5rem);
    font-weight: 300;
    line-height: 1;
    letter-spacing: 0.05em;
    color: white;
    transition: letter-spacing 0.5s ease;
    position: relative;
    z-index: 2;
  }

  .service-box:hover .box-label { letter-spacing: 0.1em; }

  .box-sub {
    margin-top: 0.75rem;
    font-size: 0.6rem;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
    position: relative;
    z-index: 2;
    transition: color 0.4s ease;
  }

  .service-box:hover .box-sub { color: white; }

  .box-arrow {
    position: absolute;
    bottom: 3rem;
    right: 3.5rem;
    font-size: 0.7rem;
    letter-spacing: 0.2em;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    transition: color 0.4s ease, transform 0.4s ease;
    z-index: 2;
  }

  .service-box:hover .box-arrow {
    color: white;
    transform: translateX(6px);
  }

  /* ─── Vault section ───────────────────────────────────────────────────── */
  .vault-section {
    width: 100%;
    padding: 5rem 4rem;
    border-top: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3rem;
    opacity: 0;
    animation: fadeUp 1s cubic-bezier(0.22, 1, 0.36, 1) 1s forwards;
  }

  .vault-header { text-align: center; }

  .vault-eyebrow {
    font-size: 0.55rem;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: var(--text-faint);
    margin-bottom: 1rem;
  }

  .vault-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(1.5rem, 3vw, 2.5rem);
    font-weight: 300;
    font-style: italic;
    color: var(--text-body);
    letter-spacing: 0.08em;
  }

  /* ─── Code input ──────────────────────────────────────────────────────── */
  .vault-input-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    width: 100%;
    max-width: 320px;
  }

  .code-input {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 1rem 0;
    font-family: 'Montserrat', sans-serif;
    font-size: 2rem;
    font-weight: 300;
    letter-spacing: 0.5em;
    color: var(--text-primary);
    text-align: center;
    outline: none;
    transition: border-color 0.3s ease;
    -moz-appearance: textfield;
  }

  .code-input::-webkit-outer-spin-button,
  .code-input::-webkit-inner-spin-button { -webkit-appearance: none; }

  .code-input::placeholder { color: var(--text-faint); }
  .code-input:focus { border-color: var(--text-body); }
  .code-input.error { border-color: #aa3333; }
  .code-input.success { border-color: #2e2e2e; }

  .input-hint {
    font-size: 0.55rem;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--text-muted);
    text-align: center;
    height: 1rem;
    transition: color 0.3s ease;
  }

  .input-hint.error { color: #aa3333; }
  .input-hint.success { color: var(--text-body); }

  /* ─── Proceed button ──────────────────────────────────────────────────── */
  .proceed-btn {
    display: inline-block;
    padding: 0.9rem 3rem;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-body);
    font-family: 'Montserrat', sans-serif;
    font-size: 0.6rem;
    font-weight: 400;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    cursor: pointer;
    text-decoration: none;
    opacity: 0;
    pointer-events: none;
    transform: translateY(8px);
    transition: opacity 0.5s ease, transform 0.5s ease, border-color 0.3s ease,
                color 0.3s ease, background 0.3s ease;
  }

  .proceed-btn.visible {
    opacity: 1;
    pointer-events: all;
    transform: translateY(0);
  }

  .proceed-btn:hover {
    border-color: var(--text-primary);
    color: var(--text-primary);
    background: rgba(0,0,0,0.04);
  }

  /* ─── Footer ──────────────────────────────────────────────────────────── */
  .site-footer {
    width: 100%;
    padding: 2rem 4rem;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .footer-copy {
    font-size: 0.55rem;
    letter-spacing: 0.25em;
    color: var(--text-faint);
    text-transform: uppercase;
  }

  .footer-domain {
    font-family: 'Cormorant Garamond', serif;
    font-size: 0.75rem;
    letter-spacing: 0.2em;
    color: var(--text-muted);
    font-style: italic;
  }

  /* ─── Animations ──────────────────────────────────────────────────────── */
  @keyframes fadeDown {
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeUp {
    to { opacity: 1; transform: translateY(0); }
  }

  /* ─── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width: 768px) {
    .top-bar { padding: 1rem 2rem; flex-direction: column; gap: 1rem; align-items: flex-start; }
    .top-bar-left { flex-direction: column; gap: 1rem; width: 100%; }
    .contact-info { flex-direction: column; gap: 0.75rem; align-items: flex-start; width: 100%; }
    .whatsapp-btn { width: 100%; justify-content: center; }
    .site-header { padding: 2rem; flex-direction: column; align-items: flex-start; gap: 0.5rem; }
    .hero-grid { grid-template-columns: 1fr; }
    .service-box.box-interiors { border-right: none; border-bottom: 1px solid var(--border); }
    .service-box { min-height: 40vh; }
    .service-box.box-services { min-height: 40vh; }
    .logo-img { height: 36px; }
    .top-logo-img { height: 28px; }
    .vault-section { padding: 3rem 2rem; }
    .site-footer { padding: 1.5rem 2rem; flex-direction: column; gap: 0.5rem; text-align: center; }
  }
`;

/* ─── API base — update to your actual Flask URL in production ─────────────── */
const API_BASE =
  process.env.NODE_ENV === "production"
    ? "/api"
    : "http://localhost:5000/api";

export default function App() {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle"); // idle | checking | valid | invalid
  const [proceedUrl, setProceedUrl] = useState(null);
  const inputRef = useRef(null);

  /* Inject styles */
  useEffect(() => {
    const tag = document.createElement("style");
    tag.textContent = styles;
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  /* Verify code whenever 4 digits are entered */
  useEffect(() => {
    if (code.length !== 4) {
      setStatus("idle");
      setProceedUrl(null);
      return;
    }

    setStatus("checking");

    fetch(`${API_BASE}/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: parseInt(code, 10) }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) {
          if (data.redirect) {
            // Specific redirect — navigate immediately
            window.location.href = data.redirect;
          } else {
            setStatus("valid");
            setProceedUrl(null); // Generic unlock — button links to default
          }
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => setStatus("invalid"));
  }, [code]);

  const hintText = {
    idle: "enter your 4-digit access code",
    checking: "verifying...",
    valid: "access granted",
    invalid: "invalid code",
  }[status];

  const inputClass = `code-input${status === "invalid" ? " error" : status === "valid" ? " success" : ""}`;
  const hintClass = `input-hint${status === "invalid" ? " error" : status === "valid" ? " success" : ""}`;

  return (
    <div className="site-wrapper">
      {/* ── Top Bar with Contact ── */}
      <div className="top-bar">
        <div className="top-bar-left">
          <a href="/" className="top-logo">
            <img src="/logo.png" alt="SkyConsults" className="top-logo-img" />
          </a>
          <div className="contact-info">
            <a href="tel:+919008827003" className="contact-item">
              <span className="contact-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
              </span>
              <span>+91 900 8827 003</span>
            </a>
            <a href="mailto:Invest@skyconsults.in" className="contact-item">
              <span className="contact-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <span>Invest@skyconsults.in</span>
            </a>
          </div>
        </div>
        <a
          href="https://wa.me/919008827003"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-btn"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M11.99 2C6.472 2 2 6.472 2 11.99c0 1.883.52 3.636 1.416 5.14L2 22l5.01-1.396A9.944 9.944 0 0011.99 22C17.508 22 22 17.528 22 12.01 22 6.493 17.508 2 11.99 2zm0 18c-1.618 0-3.12-.46-4.402-1.25l-.314-.187-3.258.908.917-3.17-.205-.326A7.954 7.954 0 014 12.01C4 7.58 7.573 4 11.99 4c4.418 0 8.01 3.58 8.01 8.01C20 16.427 16.408 20 11.99 20z"/>
          </svg>
          <span>WhatsApp</span>
        </a>
      </div>

      {/* ── Header ── */}
      <header className="site-header">
        <a href="/" className="logo">
          <img src="/logo.png" alt="SkyConsults" className="logo-img" />
        </a>
        <p className="header-tagline">Spaces · Properties · Possibilities</p>
      </header>

      {/* ── Hero boxes ── */}
      <div className="hero-grid">
        <a
          href="https://interiors.skyconsults.in"
          className="service-box box-interiors"
          rel="noopener noreferrer"
        >
          <div className="box-line-top" />
          <span className="box-number">01</span>
          <h2 className="box-label">Interiors</h2>
          <p className="box-sub">Design · Space Planning · Execution</p>
          <span className="box-arrow">Visit →</span>
        </a>

        <a
          href="https://realestate.skyconsults.in"
          className="service-box box-realestate"
          rel="noopener noreferrer"
        >
          <div className="box-line-top" />
          <span className="box-number">02</span>
          <h2 className="box-label">Real Estate</h2>
          <p className="box-sub">Acquisition · Advisory · Investment</p>
          <span className="box-arrow">Visit →</span>
        </a>

        <a
          href="https://services.skyconsults.in"
          className="service-box box-services"
          rel="noopener noreferrer"
        >
          <div className="box-line-top" />
          <span className="box-number">03</span>
          <h2 className="box-label">Services</h2>
          <p className="box-sub">Specialized Cleaning · Plumbing · Electrical · Misc</p>
          <span className="box-arrow">Visit →</span>
        </a>
      </div>

      {/* ── Client Vault ── */}
      <section className="vault-section">
        <div className="vault-header">
          <p className="vault-eyebrow">Restricted</p>
          <h3 className="vault-title">Client Access</h3>
        </div>

        <div className="vault-input-wrap">
          <input
            ref={inputRef}
            type="number"
            className={inputClass}
            placeholder="_ _ _ _"
            maxLength={4}
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 4);
              setCode(val);
            }}
            aria-label="Client access code"
          />
          <p className={hintClass}>{hintText}</p>

          <a
            href={proceedUrl || "https://realestate.skyconsults.in"}
            className={`proceed-btn${status === "valid" ? " visible" : ""}`}
            rel="noopener noreferrer"
          >
            Proceed
          </a>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="site-footer">
        <span className="footer-copy">© {new Date().getFullYear()} SkyConsults. All rights reserved.</span>
        <span className="footer-domain">skyconsults.in</span>
      </footer>
    </div>
  );
}
